/**
 * Transform benefits for saving to the backend API
 *
 * This module handles the transformation of benefit data from frontend grid format
 * to the backend API format, including:
 * - Adding the required objectType discriminator
 * - Filtering out fields that don't belong to the coverage type
 * - Applying coverage-specific transformations
 *
 * Supports two patterns:
 * - New pattern: LOCs in LOC_MAP - transform already includes objectType and filtering
 * - Old pattern: LOCs with individual transform functions - applies transform, then adds objectType and filters
 */

import { logger } from "@platform/browser-logger";
import { type BenefitTransformToApiOptions } from "@platform/btx-benefit-transforms";

import { LOC_MAP, type LocCoverageType } from "../locs/locMap";

/**
 * Transforms benefit plan benefits to match the expected backend structure
 *
 * This function supports two patterns during the migration to the new LOC-based architecture:
 *
 * **New Pattern (LOCs in LOC_MAP):**
 * - Transform functions in LOC modules handle everything:
 *   - Apply coverage-specific transformations
 *   - Add objectType discriminator
 *   - Include only valid fields
 * - Result is returned directly, no additional processing needed
 *
 * **Old Pattern (Legacy LOCs):**
 * - Uses individual transform functions from api/transforms/
 * - Requires post-processing:
 *   - Add objectType discriminator
 *   - Filter to only valid fields
 *
 * As LOCs are migrated to the new pattern, they will be added to LOC_MAP and automatically
 * use the new pattern path.
 *
 * @param benefits - The benefits object from the frontend grid (flat fields)
 * @param coverageType - The coverage type (e.g., "cancer", "hospitalIndemnity", "dental-ppo")
 * @returns Transformed benefits object matching backend API structure (nested objects with objectType)
 * @public Exported for testing
 */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any */
export const transformBenefitsForSave = (
  benefits: any,
  coverageType: LocCoverageType,
  options?: BenefitTransformToApiOptions,
): any => {
  /* eslint-enable @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any */
  if (!benefits || typeof benefits !== "object") {
    return benefits;
  }

  const locConfig = LOC_MAP[coverageType];
  const transformedBenefits = locConfig.transformToApi(benefits, options);
  logger.debug(
    `[BTX API] Transformed ${coverageType} benefits using LOC map:`,
    {
      transformedBenefits,
    },
  );
  return transformedBenefits;
};
